<script setup>
import { ref, computed, watchEffect } from 'vue'
import { useRoute, createError, useNuxtApp } from '#imports'
import berlinLocations from '~/data/berlin'
import { eventTypeMap } from '~/data/eventTypes'

// Komponenten importieren
import HeroSection from '~/components/landing/HeroSection.vue'
import FeaturesSection from '~/components/landing/FeaturesSection.vue'
import GallerySection from '~/components/landing/GallerySection.vue'
import PackagesSection from '~/components/landing/PackagesSection.vue'
import TestimonialsSection from '~/components/landing/TestimonialsSection.vue'
import FaqSection from '~/components/landing/FaqSection.vue'
import ProcessSection from '~/components/landing/ProcessSection.vue'
import LocationSection from '~/components/landing/LocationSection.vue'
import SiteFooter from '~/components/SiteFooter.vue'
import SeoHead from '~/components/SeoHead.vue'
import ContactForm from '~/components/landing/ContactForm.vue'

const route = useRoute()
const district = ref('')
const eventType = ref('')
const location = ref(null)
const heroTitle = ref('')
const heroText = ref('')
const pageMeta = ref({
  title: '',
  description: '',
  canonicalUrl: '',
  ogImage: ''
})


// Standardpakete
const packages = [
  {
    title: 'Digital-Paket',
    description: 'Ideal für moderne Events mit digitalem Fokus.',
    features: [
      'Moderne Fotobox mit Touchscreen',
      'Unbegrenzte digitale Fotos',
      'Digitale Galerie mit Download',
      'Social Media Sharing',
      'Requisiten-Set',
      'Auf-/Abbau inklusive'
    ],
    isPopular: false
  },
  {
    title: 'Premium-Paket',
    description: 'Das Rundum-sorglos-Paket für Ihr Event.',
    features: [
      'Hochwertige Fotobox mit DSLR',
      'Bis zu 400 Foto-Ausdrucke',
      'Unbegrenzte digitale Fotos',
      'Premium Requisiten',
      'Individuelles Layout',
      'Digitale Galerie mit Download',
      'Social Media Sharing',
      'Auf-/Abbau inklusive'
    ],
    isPopular: true
  },
  {
    title: 'Standard-Paket',
    description: 'Der perfekte Einstieg mit hochwertigen Ausdrucken.',
    features: [
      'Moderne Fotobox mit Touchscreen',
      'Bis zu 200 Foto-Ausdrucke',
      'Unbegrenzte digitale Fotos',
      'Standard-Requisiten',
      'Digitale Galerie',
      'Auf-/Abbau inklusive'
    ],
    isPopular: false
  }
]

const {
  $generateLocalBusinessSchema,
  $generateFaqSchema,
  $generateServiceSchema,
  $generateBreadcrumbSchema
} = useNuxtApp()

const businessInfo = computed(() => {
  if (!location.value || !eventType.value) return {}

  return {
    name: 'mietfotobox',
    description: 'Fotobox Vermietung aus Berlin für Hochzeiten, Geburtstage und Events.',
    url: 'https://mietfotobox.com',
    telephone: '+4917641620316',
    address: {
      street: 'Am Dörferweg 14',
      city: 'Berlin',
      zip: '13057',
      country: 'DE'
    },
    priceRange: '€€',
    openingHours: 'Mo-Su 08:00-22:00'
  }
})

const faqItems = computed(() => {
  if (!location.value || !eventType.value) return []

  const eventInfo = eventTypeMap[eventType.value]
  if (!eventInfo) return []

  const defaultFaqs = [
    {
      question: `Welche Leistungen sind bei einer Fotobox für ${eventInfo.name} enthalten?`,
      answer: 'Im Preis enthalten sind Aufbau, ausreichend Requisiten, hochwertige Ausdrucke, eine digitale Galerie sowie die persönliche Betreuung rund um Ihre Veranstaltung.'
    },
    {
      question: `Wie lange kann ich die Fotobox in ${location.value.name} mieten?`,
      answer: `Sie können unsere Fotobox in ${location.value.name} flexibel für Ihre Veranstaltung mieten. Längere Mietdauern sind auf Anfrage ebenfalls möglich.`
    },
    {
      question: `Welche Leistungen sind bei der Fotobox Miete inklusive?`,
      answer: 'Inklusive sind eine professionelle Fotobox mit hochwertiger Kamera, Requisiten, Ausdrucke, digitale Online-Galerie, Aufbau und Abbau sowie ein individuelles Layout.'
    },
    {
      question: 'Wie schnell sind die Fotos verfügbar?',
      answer: 'Die Fotos werden direkt vor Ort ausgegeben und zusätzlich digital bereitgestellt. So können Ihre Gäste die Erinnerungen sofort mitnehmen und später online abrufen.'
    },
    {
      question: `Gibt es eine Anfahrtspauschale für ${location.value.name}?`,
      answer: `Nein, die Anfahrt nach ${location.value.name} ist bereits im Angebot berücksichtigt. Auch Aufbau und Abbau der Fotobox sind inklusive.`
    }
  ]

  return location.value.faqItems && location.value.faqItems.length > 0
    ? location.value.faqItems
    : defaultFaqs
})

const localBusinessData = computed(() =>
  businessInfo.value && Object.keys(businessInfo.value).length > 0
    ? $generateLocalBusinessSchema(businessInfo.value)
    : {}
)

const faqData = computed(() =>
  faqItems.value && faqItems.value.length > 0
    ? $generateFaqSchema(faqItems.value)
    : {}
)

const serviceData = computed(() => {
  if (!location.value || !eventType.value) return {}

  const eventInfo = eventTypeMap[eventType.value]

  const serviceInfo = {
    url: pageMeta.value.canonicalUrl || '',
    name: `Fotobox für ${eventInfo?.name || 'Events'} in ${location.value.name}`,
    description: `Professionelle Fotobox Vermietung für ${eventInfo?.name || 'Events'} in ${location.value.name}. Inklusive Aufbau, Requisiten, Ausdrucke und digitaler Galerie.`,
    areaServed: location.value.name,
  }

  return $generateServiceSchema(serviceInfo)
})

const breadcrumbData = computed(() => {
  if (!location.value || !eventType.value || !pageMeta.value.canonicalUrl) return {}

  const eventInfo = eventTypeMap[eventType.value]
  if (!eventInfo) return {}

  return $generateBreadcrumbSchema([
    {
      name: 'mietfotobox.com',
      item: 'https://mietfotobox.com/'
    },
    {
      name: 'Berlin',
      item: 'https://mietfotobox.com/berlin/'
    },
    {
      name: location.value.name,
      item: `https://mietfotobox.com/berlin/${district.value}/`
    },
    {
      name: eventInfo.name,
      item: pageMeta.value.canonicalUrl
    }
  ])
})

const structuredData = computed(() => {
  const data = []

  if (localBusinessData.value && Object.keys(localBusinessData.value).length > 0) {
    data.push(localBusinessData.value)
  }

  if (serviceData.value && Object.keys(serviceData.value).length > 0) {
    data.push(serviceData.value)
  }

  if (faqData.value && Object.keys(faqData.value).length > 0) {
    data.push(faqData.value)
  }

  if (breadcrumbData.value && Object.keys(breadcrumbData.value).length > 0) {
    data.push(breadcrumbData.value)
  }

  return data
})
// Zentrale Funktion zum Laden der Location-Daten
watchEffect(() => {
  if (!route.params.district || !route.params.eventType) return
  
  district.value = route.params.district
  eventType.value = route.params.eventType
  
  // Map district to location key (berlin-mitte -> berlin-mitte)
  const locationKey = `berlin-${district.value.replace('berlin-', '')}`
  
  // Check if event type is valid
  if (!eventTypeMap[eventType.value]) {
    throw createError({ statusCode: 404, message: 'Event type not found' })
  }
  
  // Check if location exists
  if (!berlinLocations[locationKey]) {
    throw createError({ statusCode: 404, message: 'Location not found' })
  }
  
  location.value = berlinLocations[locationKey]
  const loc = location.value
  const eventInfo = eventTypeMap[eventType.value]

  heroTitle.value = `${eventInfo.title} ${loc.name} mieten | TOP Photobooth Berlin`
  heroText.value = `🎉 Professionelle Fotobox ${loc.name} | ✓ Sofortige Ausdrucke ✓ Requisitenbox ✓ Digitale Galerie ✓ Kostenlose Anfahrt | Perfekt für ${eventInfo.name}!`

  pageMeta.value = {
    title: `${eventInfo.title} ${loc.name} mieten | mietfotobox.com`,
    description: `${eventInfo.title} ${loc.name} ✓ Requisiten inklusive ✓ Sofortige Ausdrucke ✓ Digitale Galerie ✓ Aufbau inklusive ✓ Jetzt anfragen!`,
    canonicalUrl: `https://mietfotobox.com/berlin/${district.value}/${eventType.value}/`,
    ogImage: 'https://mietfotobox.com/static/assets/hero.webp',
  }
})
</script>

<template>
  <div v-if="location && eventType">
    <SeoHead 
      :title="pageMeta.title"
      :description="pageMeta.description"
      :canonicalUrl="pageMeta.canonicalUrl"
      :ogImage="pageMeta.ogImage"
      :structuredData="structuredData"
    />

    <HeroSection 
      :title="heroTitle" 
      :introText="heroText" 
      :heroImage="location.heroImage || 'https://mietfotobox.com/static/assets/hero.webp'" 
    />
    
    <FeaturesSection 
      :locationName="location.name" 
      :eventType="eventTypeMap[eventType]?.name || 'Event'" 
    />
    
    <GallerySection 
      :locationName="location.name" 
    />
    
    <PackagesSection 
      :packages="location.packages || packages" 
      :locationName="location.name" 
    />

    <div id="inquiry-form" class="bg-pink-700 px-4"></div>

    <ContactForm></ContactForm>

    <TestimonialsSection />
    
    <FaqSection 
      :faqItems="faqItems" 
      :locationName="location.name" 
    />
    
    <ProcessSection 
      :locationName="location.name" 
    />
    
    <LocationSection 
      :locationName="location.name" 
      :eventType="`${eventTypeMap[eventType]?.name}en` || 'Events'" 
      :venues="location.venues || []" 
      :description="location.locationDescription" 
    />
    
    <SiteFooter 
      :currentLocation="location.name" 
      :neighborLocations="location.neighborLocations || []" 
    />
  </div>
</template>
